// Input form elements such as text fields and checkboxes.

import {
    Add,
    ArrowDown,
    ArrowUp,
    CheckboxCheckedFilled,
    Checkbox as CheckboxOpen,
    ChevronDown,
    ChevronUp,
    Edit as EditIcon,
    RadioButton,
    RadioButtonChecked,
    Search,
    TrashCan,
} from '@carbon/icons-react';
import { getFontLineHeight, getFontSize } from 'platform-specific/font';
import React, { type ComponentType, useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { usePersonaKey } from 'util/datastore';
import type { Direction, TLabel } from 'util/stdtypes';
import { makeTestID, t, undefA, undefE, undefR, undefS, undefTL } from 'util/stdtypes';
import { toHoverColor, toPressColor, translucentHoverColor, translucentPressColor } from 'util/util-color';

import { HorizBox, HoverView, Pad, PadBox, SeparatedBox } from './basics';
import { ActionButton, CTAButton, SubtleButton, TextButton } from './button';
import {
    colorBorderPrimary,
    colorBorderSelected,
    colorButtonAccent,
    colorButtonPrimary,
    colorButtonSecondary,
    colorSurfacePrimary,
    colorSurfaceSecondary,
    colorTagAccentLight,
    colorTextPrimary,
    colorTextSecondary,
    colorTextWarning,
} from './color';
import { ProfilePhoto } from './people';
import { Popup, PopupTip } from './popup';
import { fontFamilySansMedium, fontFamilySansRegular, Heading, TextField, UtilityText } from './text';
import { useTranslation } from './translation';

export function FormField({
    label = undefTL,
    text = undefS,
    descriptionLabel = undefTL,
    descriptionText = undefS,
    errorLabel = undefTL,
    errorText = undefS,
    required = false,
    accessoryAction = undefE,
    children,
}) {
    const tLabel = useTranslation(label);
    return (
        <View>
            <HorizBox center>
                {required && (
                    <UtilityText
                        formatParams={{ label: tLabel }}
                        label={label ? t('{label} (required)') : undefined}
                        text={text ? text + ' (required)' : undefined}
                        type='small'
                        weight='medium'
                    />
                )}
                {!required && <UtilityText label={label} text={text} type='small' weight='medium' />}
                {accessoryAction && <View style={{ marginLeft: 'auto' }}>{accessoryAction}</View>}
            </HorizBox>
            <Pad size={8} />
            {children}
            {(errorLabel || errorText) && (
                <PadBox top={8}>
                    <UtilityText color={colorTextWarning} label={errorLabel} text={errorText} type='small' />
                </PadBox>
            )}
            {(descriptionLabel || descriptionText) && (
                <PadBox top={8}>
                    <UtilityText color={colorTextSecondary} label={descriptionLabel} text={descriptionText} type='small' />
                </PadBox>
            )}
        </View>
    );
}

const RadioContext = React.createContext({ radioSelection: null, onSetRadio: undefA });

export function RadioGroup({ children, value, onChange }) {
    const [radioSelection, setRadioSelection] = React.useState(value);

    function onSetRadio(value) {
        setRadioSelection(value);
        onChange(value);
    }
    return (
        <RadioContext.Provider value={{ radioSelection, onSetRadio }}>
            <View role='radiogroup'>{children}</View>
        </RadioContext.Provider>
    );
}

export function RadioOption({ emoji = undefS, text = undefS, radioKey = undefS, label = undefTL }) {
    const s = RadioItemStyle;
    const { radioSelection, onSetRadio } = React.useContext(RadioContext);
    const value = radioSelection === radioKey;
    return (
        <HoverView
            aria-checked={!!value}
            hoverStyle={s.hover}
            onPress={() => onSetRadio(radioKey)}
            pressedStyle={s.pressed}
            role='radio'
            testID={makeTestID({ label, text })}
        >
            <PadBox vert={8}>
                <HorizBox center>
                    {value ? (
                        <RadioButtonChecked color={colorButtonPrimary} size={32} />
                    ) : (
                        <RadioButton size={32} style={{ fill: colorBorderPrimary }} />
                    )}
                    <Pad size={12} />
                    {emoji && (
                        <PadBox right={6}>
                            <UtilityText aria-hidden text={emoji} type='tiny' weight='strong' />
                        </PadBox>
                    )}
                    <UtilityText label={label} text={text} />
                </HorizBox>
            </PadBox>
        </HoverView>
    );
}
const RadioItemStyle = StyleSheet.create({
    hover: {
        backgroundColor: translucentHoverColor(),
    },
    pressed: {
        backgroundColor: translucentPressColor(),
    },
});

export function Checkbox({
    emoji = undefS,
    text = undefS,
    label = undefTL,
    value = false,
    onChange = undefA,
    size = 32,
    ariaLabel = undefTL,
    testID = undefS,
}) {
    const s = CheckboxStyle;
    return (
        <HoverView
            ariaLabel={ariaLabel}
            hoverStyle={s.hover}
            onPress={() => onChange(!value)}
            pressedStyle={s.pressed}
            role='checkbox'
            testID={makeTestID({ testID, label, text })}
        >
            <PadBox vert={8}>
                <HorizBox center>
                    <View style={{ width: size }}>
                        {value ? (
                            <CheckboxCheckedFilled color={colorButtonPrimary} size={size} />
                        ) : (
                            <CheckboxOpen size={size} style={{ fill: colorBorderPrimary }} />
                        )}
                    </View>
                    {text || label ? <Pad size={12} /> : null}
                    {emoji && (
                        <PadBox right={6}>
                            <UtilityText aria-hidden text={emoji} type='tiny' weight='strong' />
                        </PadBox>
                    )}
                    <UtilityText label={label} numberOfLines={1} text={text} />
                </HorizBox>
            </PadBox>
        </HoverView>
    );
}
const CheckboxStyle = StyleSheet.create({
    hover: {
        backgroundColor: translucentHoverColor(),
    },
    pressed: {
        backgroundColor: translucentPressColor(),
    },
});

export function AccordionField({ titleContent, testID = undefS, defaultOpen = false, forceOpen = false, children }) {
    const s = AccordionFieldStyle;
    const [open, setOpen] = React.useState(defaultOpen);
    return (
        <View>
            <HoverView hoverStyle={s.hover} onPress={() => setOpen(!open)} pressedStyle={s.pressed} style={s.titleBar} testID={testID}>
                <HorizBox center spread>
                    {titleContent}
                    <Pad />
                    {!forceOpen && (open ? <ChevronUp color={colorTextPrimary} /> : <ChevronDown color={colorTextPrimary} />)}
                </HorizBox>
            </HoverView>
            {(open || forceOpen) && children}
        </View>
    );
}
const AccordionFieldStyle = StyleSheet.create({
    titleBar: {
        minHeight: 48,
        justifyContent: 'center',
        paddingRight: 16,
        // paddingHorizontal: 16
    },
    hover: {
        backgroundColor: translucentHoverColor(),
    },
    pressed: {
        backgroundColor: translucentPressColor(),
    },
});

export function Toggle({
    emoji = undefS,
    text = undefS,
    label = undefTL,
    infoText = undefS,
    infoLabel = undefTL,
    value = false,
    spread = false,
    onChange = undefA,
    testID = undefS,
}) {
    const s = ToggleStyle;
    const tLabel = useTranslation(label);
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);

    const toggleZoneBaseStyle = [s.toggleZone, isHovered ? s.toggleZoneHovered : null, isPressed ? s.toggleZonePressed : null];
    return (
        <PadBox vert={8}>
            <HorizBox center shrink spread={spread}>
                <HorizBox center shrink>
                    {emoji && (
                        <PadBox right={8}>
                            <UtilityText aria-hidden text={emoji} type='tiny' weight='strong' />
                        </PadBox>
                    )}
                    <UtilityText label={label} text={text} />
                    {(infoLabel || infoText) && (
                        <PadBox left={4}>
                            <PopupTip label={infoLabel} text={infoText} />
                        </PadBox>
                    )}
                </HorizBox>
                <Pad size={12} />
                <HoverView
                    ariaText={value ? `${text ?? tLabel}, checked` : `${text ?? tLabel}, unchecked`}
                    onPress={() => onChange(!value)}
                    role='button'
                    setHover={setIsHovered}
                    setPressed={setIsPressed}
                    shrink
                    testID={makeTestID({ testID, label, text })}
                >
                    <View style={value ? s.toggleZoneSelected : toggleZoneBaseStyle}>
                        <View style={value ? s.toggleBallSelected : s.toggleBall} />
                    </View>
                </HoverView>
            </HorizBox>
        </PadBox>
    );
}

const ToggleStyle = StyleSheet.create({
    toggleZone: {
        width: 56,
        height: 32,
        backgroundColor: colorBorderPrimary,
        borderRadius: 100,
        ...({ transition: 'background-color 0.2s ease-in-out' } as any),
    },
    toggleBall: {
        boxShadow: '2px 0px 10px rgba(0, 0, 0, 0.30)' as any, // FIXME: boxShadow not typed for native
        elevation: 5, // for Android,
        position: 'absolute',
        left: 2,
        top: 2,
        width: 28,
        height: 28,
        borderRadius: 100,
        backgroundColor: colorButtonSecondary,
        ...({ transition: 'left 0.2s ease-in-out, background-color 0.2s ease-in-out' } as any),
    },
    toggleZoneHovered: {
        backgroundColor: toHoverColor(colorBorderPrimary),
    },
    toggleZonePressed: {
        backgroundColor: toPressColor(colorBorderPrimary),
    },
    toggleZoneSelected: {
        width: 56,
        height: 32,
        backgroundColor: colorTagAccentLight,
        borderRadius: 100,
        ...({ transition: 'background-color 0.2s ease-in-out' } as any),
    },
    toggleBallSelected: {
        backgroundColor: colorButtonAccent,
        left: 56 - 28 - 2,
        top: 2,
        width: 28,
        height: 28,
        borderRadius: 100,
        ...({ transition: 'left 0.2s ease-in-out, background-color 0.2s ease-in-out' } as any),
    },
});

export function SearchField(props) {
    return <TextField icon={Search} multiline={false} {...props} />;
}

function DropdownOption({ option, onPress }) {
    const s = DropdownOptionStyle;
    return (
        <HoverView hoverStyle={s.hover} onPress={onPress} pressedStyle={s.pressed} style={s.option} testID={option.key}>
            <UtilityText label={option.label} text={option.text} />
        </HoverView>
    );
}
const DropdownOptionStyle = StyleSheet.create({
    option: {
        height: 48,
        paddingHorizontal: 20,
        justifyContent: 'center',
    },
    hover: {
        backgroundColor: toHoverColor(colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: toPressColor(colorSurfacePrimary),
    },
});

type DropdownFieldProps = {
    placeholder: TLabel;
    value: string | null;
    onChange: (value: string) => void;
    options: { key: string; label?: TLabel; text?: string }[];
    testID?: string;
};

export function DropdownField({ placeholder, options, value, onChange, testID }: DropdownFieldProps) {
    const s = DropdownFieldStyle;
    const [hover, setHover] = useState(false);
    function popupContent({ onClose }) {
        return (
            <View>
                {options.map((option, index) => (
                    <DropdownOption
                        key={index}
                        onPress={() => {
                            onChange(option.key);
                            onClose();
                        }}
                        option={option}
                    />
                ))}
            </View>
        );
    }
    const selectedOption = options.find((option) => option.key === value);

    return (
        <Popup matchWidth popupContent={popupContent} popupStyle={s.popup} setHover={setHover}>
            <View role='button' style={[s.dropdownField, hover && s.hover]} testID={testID}>
                {selectedOption ? (
                    <UtilityText color={colorTextPrimary} label={selectedOption.label} text={selectedOption.text} type='large' />
                ) : (
                    <UtilityText color={colorTextSecondary} label={placeholder} type='large' />
                )}
                <ChevronDown size={24} />
            </View>
        </Popup>
    );
}

const DropdownFieldStyle = StyleSheet.create({
    popup: {
        padding: 0,
        borderRadius: 0,
    },
    dropdownFieldText: {
        color: colorTextPrimary,
        fontFamily: fontFamilySansMedium,
        fontSize: getFontSize(16),
    },
    dropdownField: {
        height: 48,
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'stretch',
        justifyContent: 'space-between',
        borderColor: colorBorderPrimary,
        backgroundColor: colorSurfacePrimary,
        borderWidth: 1,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 15,
        fontSize: getFontSize(16),
        lineHeight: getFontLineHeight(16, 1.5),
        fontFamily: fontFamilySansRegular,
        color: colorTextPrimary,
    },
    hover: {
        borderColor: toHoverColor(colorBorderSelected),
    },
});

export function TextFieldButton({
    placeholder = undefTL,
    testID = undefS,
    placeholderText = undefS,
    placeholderParams = undefR,
    onPress = undefA,
}) {
    const s = TextFieldButtonStyle;
    const tPlaceholder = useTranslation(placeholder, placeholderParams);
    const personaKey = usePersonaKey();
    return (
        <HoverView ariaLabel={placeholder} hoverStyle={s.hover} onPress={onPress} role='button' style={s.textFieldButton} testID={testID}>
            <HorizBox center>
                {personaKey && (
                    <PadBox right={10}>
                        <ProfilePhoto type='tiny' userId={personaKey} />
                    </PadBox>
                )}
                <Text style={s.textFieldText}>{placeholderText ?? tPlaceholder}</Text>
            </HorizBox>
            <EditIcon color={colorTextPrimary} size={24} style={{ flexShrink: 0, paddingLeft: 16 }} />
        </HoverView>
    );
}
const TextFieldButtonStyle = StyleSheet.create({
    textFieldText: {
        color: colorTextPrimary,
        fontFamily: fontFamilySansMedium,
        fontSize: getFontSize(14),
    },
    textFieldButton: {
        borderColor: colorBorderPrimary,
        backgroundColor: colorButtonSecondary,
        borderWidth: 1,
        borderRadius: 32,
        paddingHorizontal: 16,
        height: 50,
        fontFamily: fontFamilySansRegular,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        overflow: 'hidden',
    },
    hover: {
        borderColor: toHoverColor(colorBorderSelected),
    },
});

type SaveableTextFieldProps = {
    label: TLabel;
    description?: JSX.Element;
    descriptionLabel?: TLabel;
    error?: JSX.Element;
    placeholder?: TLabel;
    value: string | null;
    multiline?: boolean;
    testID?: string;
    onSave: (value: string) => void;
    onChange?: (value: string) => void;
    accessoryActionWhenEditing?: JSX.Element;
    required?: boolean;
    autoFocus?: boolean;
};

// Note that, unlike all other form elements, this one has the
// form field built it, since it contains an edit button.
// TODO: Possible refactor all fields to include their FormField headers.

export function SaveableTextField({
    label,
    value,
    onSave,
    onChange,
    description,
    descriptionLabel,
    error,
    accessoryActionWhenEditing,
    multiline = false,
    testID,
    placeholder,
    required = false,
    autoFocus = false,
}: SaveableTextFieldProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value ?? null);

    useEffect(() => {
        onChange && onChange(tempValue ?? '');
    }, [tempValue]);

    useEffect(() => {
        if (!isEditing) {
            setTempValue(value ?? '');
        }
    }, [value]);

    const editButton = <SubtleButton icon={EditIcon} label={t('Edit')} onPress={() => setIsEditing(true)} testID={`${testID}-edit`} />;

    return (
        <FormField accessoryAction={isEditing ? accessoryActionWhenEditing : editButton} label={label} required={required}>
            <TextField
                autoFocus={autoFocus}
                editable={isEditing}
                error={!!error}
                multiline={multiline}
                onChange={(value) => setTempValue(value)}
                placeholderLabel={placeholder}
                testID={testID}
                value={tempValue}
            />
            <TextFieldDescription description={description} descriptionLabel={descriptionLabel} error={isEditing ? error : undefined} />
            {isEditing && (
                <PadBox top={12}>
                    <HorizBox center gap={12} right>
                        <TextButton
                            color={colorTextSecondary}
                            label={t('Cancel')}
                            onPress={() => {
                                setTempValue(value);
                                setIsEditing(false);
                            }}
                            underline
                        />
                        <CTAButton
                            disabled={!!error && !tempValue}
                            label={t('Save')}
                            onPress={() => {
                                onSave(tempValue ?? '');
                                setIsEditing(false);
                            }}
                        />
                    </HorizBox>
                </PadBox>
            )}
        </FormField>
    );
}

function TextFieldDescription({ description, descriptionLabel, error }) {
    return (
        <>
            {description && !error && <PadBox top={12}>{description}</PadBox>}
            {descriptionLabel && !error && (
                <PadBox top={12}>
                    <UtilityText color={colorTextSecondary} label={descriptionLabel} type='small' />
                </PadBox>
            )}
            {error && <PadBox top={12}>{error}</PadBox>}
        </>
    );
}

export function PlaceholderContainer({ children }) {
    const s = PlaceholderContainerStyle;
    return <View style={s.container}>{children}</View>;
}

const PlaceholderContainerStyle = StyleSheet.create({
    container: {
        backgroundColor: colorSurfaceSecondary,
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 10,
        padding: 16,
        borderStyle: 'dashed',
    },
});

export function EditingCard({ children }) {
    const s = EditingCardStyle;
    return <View style={s.container}>{children}</View>;
}

const EditingCardStyle = StyleSheet.create({
    container: {
        backgroundColor: colorSurfacePrimary,
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 10,
        padding: 20,
    },
});

export type ListSelectorItem = {
    key: string;
    text: string;
};

type ListSelectorProps = {
    label: TLabel;
    addLabel: TLabel;
    addHintLabel?: TLabel;
    reorderable: boolean;
    items: ListSelectorItem[];
    addWidget: ComponentType<{ setToAdd: (key: string[]) => void; onSubmit: () => void }>;
    onDeleteItem: (key: string) => void;
    onAddItems: (keys: string[]) => void;
    onChangeSortOrder: (orderedKeys: string[]) => void;
};

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ListSelector({
    label,
    addLabel,
    addHintLabel,
    items,
    addWidget,
    reorderable,
    onDeleteItem,
    onAddItems,
    onChangeSortOrder,
}: ListSelectorProps) {
    const [adding, setAdding] = useState(false);
    const [toAdd, setToAdd] = useState([] as string[]);

    function onAdd() {
        setAdding(false);
        setToAdd([]);
        onAddItems(toAdd);
    }

    if (adding) {
        const AddWidget = addWidget;
        return (
            <FormField label={label}>
                <EditingCard>
                    <Heading label={addLabel} type='small' weight='strong' />
                    <Pad size={24} />
                    <AddWidget onSubmit={onAdd} setToAdd={setToAdd} />
                    <Pad size={24} />
                    <HorizBox center right>
                        <TextButton
                            label={t('Cancel')}
                            onPress={() => {
                                setAdding(false);
                                setToAdd([]);
                            }}
                        />
                        <Pad size={16} />
                        <CTAButton disabled={toAdd.length === 0} label={t('Add')} onPress={onAdd} />
                    </HorizBox>
                </EditingCard>
            </FormField>
        );
    } else {
        return (
            <FormField label={label}>
                <Pad size={4} />
                <PlaceholderContainer>
                    <SubtleButton color={colorTextPrimary} icon={Add} label={addLabel} onPress={() => setAdding(true)} />
                </PlaceholderContainer>
                {addHintLabel && (
                    <PadBox vert={12}>
                        <UtilityText color={colorTextSecondary} label={addHintLabel} type='small' />
                    </PadBox>
                )}
                <SeparatedBox>
                    {items.map((item, idx, collection) => {
                        const isTopItem = idx === 0;
                        const isBottomItem = idx === collection.length - 1;

                        function handleChangeSortOrder(direction: Direction) {
                            if (!onChangeSortOrder || !reorderable) return;
                            const neighbor = direction === 'up' ? collection[idx - 1] : collection[idx + 1];
                            if (!neighbor) return;
                            const newOrder = [...collection.map((item) => item.key)];
                            const currentIdx = newOrder.indexOf(item.key);
                            const neighborIdx = newOrder.indexOf(neighbor.key);
                            const tmp = newOrder[currentIdx];
                            newOrder[currentIdx] = newOrder[neighborIdx];
                            newOrder[neighborIdx] = tmp;
                            onChangeSortOrder(newOrder);
                        }

                        return (
                            <ListSelectorItem
                                disableOrderDown={isBottomItem}
                                disableOrderUp={isTopItem}
                                item={item}
                                key={item.key}
                                onChangeSortOrder={handleChangeSortOrder}
                                onDeleteItem={onDeleteItem}
                                reorderable={reorderable}
                            />
                        );
                    })}
                </SeparatedBox>
            </FormField>
        );
    }
}

type ListSelectorItemProps = {
    item: { key: string; text: string; order?: number };
    onChangeSortOrder?: (direction: Direction) => void;
    onDeleteItem: (key: string) => void;
    reorderable?: boolean;
    disableOrderUp?: boolean;
    disableOrderDown?: boolean;
};

function ListSelectorItem({ item, reorderable, onDeleteItem, onChangeSortOrder, disableOrderUp, disableOrderDown }: ListSelectorItemProps) {
    function handleChangeSortOrder(direction: Direction) {
        return () => {
            if (onChangeSortOrder) onChangeSortOrder(direction);
        };
    }

    return (
        <View style={{ height: 48, justifyContent: 'center', display: 'flex' }}>
            <HorizBox center spread testID={item.text}>
                <View>
                    <UtilityText text={item.text} />
                </View>

                <Pad size={8} />

                <HorizBox gap={8}>
                    <ActionButton icon={TrashCan} onPress={() => onDeleteItem(item.key)} testID='delete' />
                    {reorderable && (
                        <>
                            <ActionButton disabled={disableOrderUp} icon={ArrowUp} onPress={handleChangeSortOrder('up')} />
                            <ActionButton disabled={disableOrderDown} icon={ArrowDown} onPress={handleChangeSortOrder('down')} />
                        </>
                    )}
                </HorizBox>
            </HorizBox>
        </View>
    );
}
